import * as Sentry from '@sentry/react';
import * as superagent from 'superagent';
import { v4 as randomUuid } from 'uuid';
import { Store, Update, Write } from 'dg-web-shared/lib/Flux';
import {
    AbortableRequest,
    makePendingResponse,
    makeResponse as makeHttpResponse,
    Response,
} from 'dg-web-shared/lib/HttpResponse';

export type { Response } from 'dg-web-shared/lib/HttpResponse';

export type RawRequest = superagent.Request;

export interface RequestWrite {
    (store: Store, res: Response): void;
}

function requestMetadata(req: RawRequest): AbortableRequest {
    return {
        abort: () => {
            req.abort();
        },
        url: req.url,
        method: req.method,
        payload: '_data' in req ? req._data : undefined,
    };
}

export function makeResponse(
    err: unknown,
    req: RawRequest,
    sRes: superagent.Response | undefined,
): Response {
    const res = makeHttpResponse(
        err,
        requestMetadata(req),
        sRes === undefined
            ? undefined
            : {
                  status: sRes.status,
                  type: sRes.type,
                  text: sRes.text,
                  headers: sRes.headers,
                  body: sRes.body,
                  url: sRes.xhr?.responseURL,
                  readyState: sRes.xhr?.readyState,
              },
    );
    if (res.statusCode.cls.serverError) {
        Sentry.withScope(scope => {
            scope.setContext('response body', {
                value: sRes?.text || 'no body',
            });
            const requestUrl = res.request.url;
            Sentry.captureMessage(`${requestUrl} ${res.statusCode.statusCode}`);
        });
    }
    return res;
}

/*
this is the legacy request pattern, do not use this. Instead, use requestV2
@deprecated use useServerFetch
*/
export function request(req: RawRequest, requestUpdate: RequestWrite): Write {
    return (store: Store): void => {
        requestUpdate(store, makePendingResponse(requestMetadata(req)));
        req.end((err: unknown, superagentRes: superagent.Response) => {
            const res = makeResponse(err, req, superagentRes);
            requestUpdate(store, res);
            store.emitChange();
        });
    };
}

/*
@deprecated use useServerFetch
 */
export function requestV2<V>(
    reqGen: (args: V) => RawRequest,
    requestUpdate: RequestWrite,
): Update<V> {
    return (store: Store, args?: V): string => {
        const req = reqGen(args!);
        req.set(IDEMPOTENCE_TOKEN_HTTP_HEADER_NAME, randomUuid());

        const pendingName =
            requestUpdate(store, makePendingResponse(requestMetadata(req))) +
            '-requestPending-log-ignore';

        req.end((err: unknown, superagentRes: superagent.Response) => {
            const res = makeResponse(err, req, superagentRes);
            store.update(
                (store: Store, res?: Response) =>
                    requestUpdate(store, res!) + '-requestReturned-log-ignore',
                res,
            );
        });

        return pendingName;
    };
}

export const IDEMPOTENCE_TOKEN_HTTP_HEADER_NAME = 'Parkingpay-Request-UUID';
